/**
 * Parses a date string the way the `ControlUI` picker for that field type writes
 * one, so a value typed, pasted, imported or fetched lands on the instant the
 * picker would have stored. Consumers: `coerceSignifyFieldValue`,
 * `coerceToColumnValue`, `parseCsvImport`.
 *
 * `date` and `dateRange` are calendar days, so the parsed day is anchored at start
 * of day in `defaultTimeZone` (SYD-2507). Every other type is a true instant and
 * keeps the browser-local reading, which is why a bare `2026-04-01` and a
 * `2026-04-01 00:00` pasted into the same `dateTime` column land together.
 * A `YYYY-MM-DD` string goes to `dayjs` in strict mode, because `new Date` reads
 * that one shape as UTC midnight (SYD-2464) and rolls `2023-02-30` into March.
 *
 * Known limitation: an instant-bearing string (`2026-04-01T00:00:00Z`) pasted or
 * imported into a `date` column derives its calendar day browser-local before
 * being anchored in `defaultTimeZone`, so a Chicago operator copying a `date`
 * column as plain text and a Los Angeles colleague pasting it lands every cell a
 * day earlier. Deliberately not fixed here: internal same-column copy carries the
 * `data-ar-cell` envelope and pastes verbatim, so only cross-zone plain text is
 * affected.
 */
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Timestamp } from 'firebase/firestore';
import { DATE_ONLY_FIELD_DATA_TYPES } from '#types/System.FieldTypes.types';
import { dateOnlyTimestamp } from './dateOnlyTimestamp';

dayjs.extend(customParseFormat);

const DATE_ONLY_ISO = /^\d{4}-\d{2}-\d{2}$/;

export const timestampFromDateString = (raw: string, fieldDataTypeId: string): Timestamp | null => {
  const parsed = DATE_ONLY_ISO.test(raw) ? dayjs(raw, 'YYYY-MM-DD', true) : dayjs(new Date(raw));
  if (!parsed.isValid()) return null;
  return DATE_ONLY_FIELD_DATA_TYPES.has(fieldDataTypeId)
    ? dateOnlyTimestamp(parsed)
    : Timestamp.fromDate(parsed.toDate());
};
